import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import tinycolor from 'tinycolor2';
import { guid } from '@/mixins/guid';
import { editHistory } from '@/mixins/history';
import fonts from '@editor/fonts.json';
import fontColors from './font-colors.json';
import fontSymbols from './font-symbols.json';

export default Vue.component('inline-text', {
  template: require('./template.html'),
  name: 'inline-text',
  mixins: [guid, editHistory],
  computed: {
    ...mapGetters(['currentPage', 'currentSite', 'browser', 'activeDroplet']),
    buttons() {
      const opts = _.cloneDeep(this.options || this.defaults);
      const colorOption = opts.find(item => item.type === 'foreColor');
      const palette = this.currentSite.colorPalette;
      if (colorOption && palette && palette.length > 0) {
        colorOption.colors = colorOption.colors.concat(['break'],
          palette.map(color => this._concatPicker(color.value)),
        );
      }
      return opts;
    },
    filteredFonts() {
      const webSafe = _.find(this.defaults, { type: 'fontName' });
      const customFonts = this.currentSite.fonts;
      const newList = webSafe.fonts.concat(fonts.list, customFonts).sort();
      const f = new RegExp(this.searchFonts, 'gi');
      return _.filter(newList, font => font.match(f));
    },
    roamingTemplate() {
      return this.drp && this.currentPage.type !== 'template' && this.drp.templateRegion;
    },
  },
  data() {
    return {
      defaults: [
        {
          type: 'bold',
          tip: 'Bold',
        },
        {
          type: 'italic',
          tip: 'Italic',
        },
        {
          type: 'underline',
          tip: 'Underline',
        },
        {
          type: 'strikeThrough',
          tip: 'Strike',
        },
        {
          type: 'fontSize',
          tip: 'Font Size',
          sizes: [
            '8',
            '9',
            '10',
            '11',
            '12',
            '14',
            '16',
            '18',
            '20',
            '22',
            '24',
            '36',
            '42',
            '48',
            '54',
            '60',
            '72',
          ],
        },
        {
          type: 'fontName',
          tip: 'Font',
          fonts: fonts.webSafe,
        },
        {
          type: 'foreColor',
          tip: 'Font Color',
          colors: fontColors,
        },
        {
          type: 'symbols',
          tip: 'Symbols',
          symbols: fontSymbols,
        },
        {
          type: 'insertUnorderedList',
          tip: 'Unordered List',
        },
        {
          type: 'insertOrderedList',
          tip: 'Ordered List',
        },
        {
          type: 'indent',
          tip: 'Indent',
        },
        {
          type: 'justifyLeft',
          tip: 'Justify Left',
        },
        {
          type: 'justifyCenter',
          tip: 'Justify Center',
        },
        {
          type: 'justifyRight',
          tip: 'Justify Right',
        },
        {
          type: 'createLink',
          tip: 'Link',
        },
      ],
      showToolBar: false,
      savedSel: '',
      selInfo: null,
      coords: {
        arrow: {
          right: 0,
          left: 0,
        },
        toolbar: {
          right: 'unset',
          left: 'unset',
        },
        text: {
          top: 0,
          left: 0,
        },
      },
      tipContainer: null,
      toolbarBuffer: 50,
      hex: false,
      hexVal: '#',
      dropdowns: {
        foreColor: false,
        fontSize: false,
        fontName: false,
        createLink: false,
        symbols: false,
      },
      searchFonts: '',
      link: '',
      linkError: false,
      newWindow: false,
      timeout: null,
      activeStyles: [],
      actives: {
        foreColor: null,
        fontName: null,
        fontSize: null,
      },
      selectedNodes: [],
      found: {
        first: false,
        last: false,
      },
      saveDebounce: null,
      markup: '',
    };
  },
  props: {
    text: {
      type: [String, Boolean],
      required: true,
    },
    options: {
      type: Array,
      required: false,
    },
    drp: {
      type: Object,
      required: false,
    },
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeFn, false);
    window.removeEventListener('scroll', this.scrollFn, false);
  },
  mounted() {
    this.markup = this.text;
    this.tipContainer = document.getElementById('drzInlineToolbar-container');
  },
  watch: {
    showToolBar(o) {
      const overlay = this.$refs.inlineOverlay;
      const wrapper = this.$refs.inlineOverlayWrapper;
      const app = document.getElementById('drzBubble-overlay-container');
      if (o) {
        this.getTextCoords(o);
        this.moveToolBar(o);
        // if there happen to be open droplet bubbles from other droplets, we need
        // to close them all.
        if (app.hasChildNodes()) {
          _.forEach(app.childNodes, (child) => {
            child.click();
          });
        }
        this.$emit('open-toolbar', true);
        this.$store.dispatch('setToolBarStatus', true);
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
        // after dom updates with visible toolbar
        this.$nextTick().then(() => {
          this.setToolBarCoords();
          this.timer = null;
          window.addEventListener('scroll', this.scrollFn, false);
          window.addEventListener('resize', this.resizeFn, false);
          app.appendChild(overlay);
        });
      } else {
        const content = this.$refs.content.innerHTML;
        this.$emit('update-text', content);
        this.$emit('open-toolbar', false);
        this.$store.dispatch('setToolBarStatus', false);
        wrapper.appendChild(overlay);
        window.removeEventListener('scroll', this.scrollFn, false);
        window.removeEventListener('resize', this.resizeFn, false);
        if (this.drp) {
          this.setEditHistory({
            type: 'inline_text',
            meta: {
              dropletType: this.drp.type,
            },
          });
        }
        // move toolbar back after hide animation runs
        setTimeout(() => {
          this.getTextCoords(o);
          this.moveToolBar(o);
        }, 250);
      }
    },
    'activeDroplet._id': function checkDroplets(id) {
      if (id) {
        this.showToolBar = false;
      }
    },
    globalOpenModal(open) {
      if (open && this.showToolBar) {
        this.showToolBar = false;
      }
    },
  },
  methods: {
    saveTextState() {
      clearTimeout(this.saveDebounce);
      this.saveDebounce = setTimeout(() => {
        const content = this.$refs.content.innerHTML;
        this.$emit('update-text', content);
      }, 500);
    },
    toggleToolBar(v) {
      this.showToolBar = v;
    },
    getTextCoords(open) {
      let top = 0;
      let left = 0;
      if (open) {
        const $el = this.$refs.content;
        const trigger = $el.getBoundingClientRect();
        top = ~~(trigger.top) - this.toolbarBuffer;
        left = ~~(trigger.left);
      }
      if (top < 45) {
        // don't allow toolbar to go out of view of the top
        top = 45;
      }
      this.coords.text.top = top;
      this.coords.text.left = left;
    },
    scrollFn() {
      if (this.timer !== null) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => this.getTextCoords(this.showToolBar), 10);
    },
    moveToolBar(o) {
      const toolbar = this.$refs.toolbar;
      if (o) {
        // move to tip container
        this.tipContainer.appendChild(toolbar);
      } else {
        // prepend back to component
        this.$el.prepend(toolbar);
      }
    },
    resetBar() {
      this.showToolBar = false;
      this.savedSel = '';
      this.selInfo = null;
      this.hex = false;
      this.hexVal = '#';
      this.dropdowns = {
        foreColor: false,
        fontSize: false,
        createLink: false,
        fontName: false,
        symbols: false,
      };
      this.link = '';
      this.linkError = false;
      this.newWindow = false;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.coords = {
          arrow: {
            right: 0,
            left: 0,
          },
          toolbar: {
            right: 'unset',
            left: 'unset',
          },
          text: {
            top: 0,
            left: 0,
          },
        };
      }, 250);
    },
    saveInput() {
      this.resetBar();
    },
    checkActive(t) {
      // helper to set dropdown buttons as active
      let cls = '';
      if (this.dropdowns[t]) {
        cls = 'drz-inlineEditor-btnActive';
      }
      return cls;
    },
    isHex() {
      this.hex = this.hexVal.match(/^#([0-9a-f]{6})$/i);
    },
    saveSelection() {
      if (window.getSelection) {
        const selection = window.getSelection();
        if (selection.getRangeAt && selection.rangeCount) {
          return selection.getRangeAt(0);
        }
      }
      return '';
    },
    resetActives() {
      this.actives = {
        foreColor: null,
        fontName: null,
        fontSize: null,
      };
    },
    checkStyles() {
      this.activeStyles = [];
      const styles = _.cloneDeep(this.defaults);
      _.forEach(styles.concat({ type: 'styleWithCSS' }), (item) => {
        if (document.queryCommandState(item.type)) {
          this.activeStyles.push(item.type);
        }
      });
      const selWrapper = this.savedSel.commonAncestorContainer.parentElement;
      // check for a custom link
      if (selWrapper && selWrapper.tagName === 'A') {
        this.link = selWrapper.getAttribute('href');
        const noActiveClass = this.activeStyles.indexOf('createLink') === -1;
        if (noActiveClass) {
          this.activeStyles.push('createLink');
        }
      }
      // check if a font name is set
      if (selWrapper && selWrapper.tagName === 'FONT') {
        const noActiveClass = this.activeStyles.indexOf('fontName') === -1;
        if (noActiveClass) {
          this.activeStyles.push('fontName');
          this.actives.fontName = selWrapper.getAttribute('face');
        }
      }
      // here we check for custom CSS styles added
      const styleList = selWrapper.getAttribute('style');
      if (styleList && this.activeStyles.includes('styleWithCSS')) {
        // check for a font color
        if (styleList.match(/color/gi)) {
          this.activeStyles.push('foreColor');
          this.actives.foreColor = (tinycolor(selWrapper.style.color).toHexString()).toUpperCase();
        }
        // check for a font size
        if (styleList.match(/font-size/gi)) {
          this.activeStyles.push('fontSize');
          this.actives.fontSize = (selWrapper.style.fontSize).replace(/\D/g, '');
        }
        // fallback check for a font family
        if (styleList.match(/font-family/gi)) {
          this.activeStyles.push('fontName');
          this.actives.fontName = selWrapper.style.fontFamily;
        }
      }
      // fallback for when the inline editer wraps text in another tag besides the a
      const wrapperParent = selWrapper.parentNode;
      if (selWrapper && wrapperParent.tagName === 'A' && wrapperParent.childNodes.length === 1) {
        this.link = wrapperParent.getAttribute('href');
        const noActiveClass = this.activeStyles.indexOf('createLink') === -1;
        if (noActiveClass) {
          this.activeStyles.push('createLink');
        }
      }
    },
    restoreSelection(rangeToRestore) {
      if (rangeToRestore) {
        if (window.getSelection) {
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(rangeToRestore);
        }
        return rangeToRestore;
      }
      return '';
    },
    checkSelection() {
      const selection = window.getSelection();
      if (selection.toString() !== '') {
        // something is selected
        this.savedSel = this.saveSelection();
        // this currently does nothing but we
        // can use this helper in the future
        this.selInfo = this.getSelectionInfo();
        this.checkStyles();
      } else {
        this.savedSel = '';
      }
    },
    toggleDropDowns(type) {
      const dds = this.dropdowns;
      _.forOwn(dds, (value, key) => {
        if (key === type) {
          dds[type] = !dds[type];
        } else {
          dds[key] = false;
        }
      });
    },
    runAction(type) {
      this.savedSel = this.saveSelection();
      if (type === 'foreColor') {
        this.toggleDropDowns('foreColor');
      } else if (type === 'fontSize') {
        this.toggleDropDowns('fontSize');
      } else if (type === 'createLink') {
        this.toggleDropDowns('createLink');
      } else if (type === 'fontName') {
        this.toggleDropDowns('fontName');
        this.searchFonts = '';
      } else if (type === 'symbols') {
        this.toggleDropDowns('symbols');
      } else {
        this.toggleDropDowns();
        document.execCommand(type);
        this.checkStyles();
      }
    },
    setFontColor(c) {
      this.restoreSelection(this.savedSel);
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('foreColor', false, c);
      // fallback when editor inlines the parent with the color and not the actual link
      const wrapper = this.savedSel.commonAncestorContainer.parentElement;
      if (wrapper && wrapper.tagName === 'A') {
        const styles = wrapper.getAttribute('style');
        if (!styles || (styles && !styles.match(/color/gi))) {
          wrapper.style.color = c;
        }
      }
      this.checkStyles();
      const noActiveClass = this.activeStyles.indexOf('foreColor') === -1;
      if (noActiveClass) {
        this.activeStyles.push('foreColor');
      }
      this.actives.foreColor = c;
    },
    setFontType(font) {
      this.restoreSelection(this.savedSel);
      document.execCommand('fontName', false, font);
      this.checkStyles();
      this.actives.fontName = font;
      const noActiveClass = this.activeStyles.indexOf('fontName') === -1;
      if (noActiveClass) {
        this.activeStyles.push('fontName');
      }
    },
    formatFontName(name) {
      const n = name.replace(/-|_/gi, ' ');
      return _.startCase(n);
    },
    findAllNodes(parent, startText, endText) {
      const children = parent.childNodes;
      if (children && children.length > 0) {
        for (const child of children) {
          const text = child.innerText;
          if (text && child.innerText === startText) {
            this.found.first = true;
          }
          if (text && this.found.first && !this.found.last) {
            this.selectedNodes.push(child);
            this.findAllNodes(child, startText, endText);
          }
          if (text && child.innerText === endText) {
            this.found.last = true;
          }
        }
      }
    },
    async setFontSize(s) {
      this.restoreSelection(this.savedSel);
      // because fontSize is so terrible, we need to hack around it to add css so
      // we set the selection to a span w/generic font size, then we get that span
      // and replace the inline font-size with inline css.
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('fontSize', false, 2);
      const span = window.getSelection().anchorNode;
      if (!_.isNull(span)) {
        const $content = this.$refs.content;
        if (this.browser.name === 'firefox') {
          const fontNodes = $content.querySelectorAll('font');
          // firefox does not use inline styleWithCSS with contenteditable and fontSize
          // because they refuse to work on this patch so we need a workaround here
          if (fontNodes && fontNodes.length > 0) {
            fontNodes.forEach((node) => {
              if (node.size === '2') {
                node.removeAttribute('size');
                node.style.fontSize = `${s}pt`;
              }
            });
          }
        } else {
          const styledNodes = $content.querySelectorAll('*[style]');
          if (styledNodes && styledNodes.length > 0) {
            styledNodes.forEach((node) => {
              if (node.style.fontSize.match(/small/gi)) {
                node.style.fontSize = `${s}pt`;
              }
            });
          }
        }
        // set the new updated text as the current selection
        this.savedSel = this.saveSelection();
      }
      this.checkStyles();
    },
    insertSymbol(item) {
      // delete contents so we can replace with a symbol
      this.savedSel.deleteContents();
      this.savedSel.insertNode(
        document.createTextNode(String.fromCharCode(item.code),
        ));
      this.restoreSelection(this.savedSel);
    },
    createLink() {
      this.restoreSelection(this.savedSel);
      if (this.link !== '') {
        const span = window.getSelection().anchorNode;
        let oldLink;
        if (!_.isNull(span) && span.parentElement.tagName === 'A') {
          // link is wrapped with the previous link, so we need those styles
          oldLink = span.parentElement;
        }
        document.execCommand('unlink', false, false);
        document.execCommand('createLink', false, this.link);
        // retreive the span element that was changed
        if (!_.isNull(span)) {
          let newLinkNode = span.parentElement;
          if (newLinkNode.tagName && newLinkNode.tagName !== 'A') {
            newLinkNode = newLinkNode.querySelectorAll('a')[0];
          }
          newLinkNode.classList.add('drz-link');
          if (oldLink) {
            // place old styles with new
            newLinkNode.setAttribute('style', oldLink.getAttribute('style'));
          }
          // set new window option if true
          if (this.newWindow) {
            newLinkNode.setAttribute('target', '_blank');
            newLinkNode.classList.add('drz-link-newTab');
          }
        }
      } else {
        this.linkError = true;
      }
      this.savedSel = this.saveSelection();
      this.checkStyles();
      const noActiveClass = this.activeStyles.indexOf('createLink') === -1;
      if (noActiveClass) {
        this.activeStyles.push('createLink');
      }
    },
    validateLink(e) {
      if (e.target.value !== '') {
        this.linkError = false;
      } else {
        this.linkError = true;
      }
    },
    setToolBarCoords() {
      const toolbar = this.$refs.toolbar;
      const content = this.$refs.content;
      const contentWidth = ~~content.offsetWidth;
      const toolbarWidth = ~~toolbar.offsetWidth;
      let leftValue;
      // if toolbar is smaller than text container
      if (toolbarWidth <= contentWidth) {
        leftValue = ((contentWidth - toolbarWidth) / 2) + this.coords.text.left;
        this.coords.toolbar.left = `${leftValue}px`;
        this.coords.toolbar.right = 'unset';
      } else if (toolbarWidth > contentWidth) {
        // if toolbar is bigger than text container
        leftValue = -((toolbarWidth - contentWidth) / 2) + this.coords.text.left;
        this.coords.toolbar.left = `${leftValue}px`;
        this.coords.toolbar.right = 'unset';
      }
      this.coords.arrow.right = '0px';
      this.coords.arrow.left = '0px';
    },
    resizeFn() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        // reset the toolbar coords if open
        if (this.showToolBar) {
          this.setToolBarCoords();
        }
      }, 250);
    },
    getSelectionInfo() {
      // Info data that will be returned by the method.
      const data = {};
      if (window.getSelection) {
        const selection = window.getSelection();
        const bodyScrollTop = document.body.scrollTop;
        const bodyScrollLeft = document.body.scrollLeft;
        // If something is selected.
        if (selection.rangeCount > 0) {
          // With cloneRange() we create a perfect abstract copy of the range.
          // We do not want to modify the real one.
          const range = selection.getRangeAt(0).cloneRange();
          let rect = range.getBoundingClientRect();
          if (rect.height === 0) {
            rect = range.getClientRects()[0];
          }
          // If the caret is on an empty line or if the range contains noting or
          // new lines only, 'rect' will be undefined.
          if (rect) {
            data.width = rect.width;
            data.height = rect.height;
            // By default, x and y are calculated at the beginning of the range.
            // We have to add body scroll values
            data.xStart = rect.left + bodyScrollLeft;
            data.yStart = rect.top + bodyScrollTop;
          }
          data.text = selection.toString();
          data.charStart = range.startOffset;
          data.charEnd = range.endOffset;
          // Collapse the range to its end.
          range.collapse(false);
          // We have to update the rect with getClientRects() because the range became a caret.
          rect = range.getClientRects()[0];
          if (rect) {
            data.xEnd = rect.left + bodyScrollLeft;
            data.yEnd = rect.top + bodyScrollTop;
          }
        }
      }
      if (data.text) {
        data.characters = data.text.replace(/\s/g, '').length;
        data.charactersAll = data.text.replace(/[\n\r]/g, '').length;
      } else {
        data.characters = 0;
        data.charactersAll = 0;
      }
      return data;
    },
    emitEvent(e, type) {
      this.$emit(type, e);
    },
    formatPaste(e, type) {
      this.$emit(type, e);
      // b-test
      const plainText = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, plainText);
      e.preventDefault();
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/_bubble-options/components/inline-text/index.js